/**
 * /affiliate/status — state router for every affiliate portal condition.
 *
 * Renders exactly one card for the current state as computed by the
 * `affiliate_portal_state` RPC. Never renders admin notes, rejection reasons
 * or risk flags — those never leave the DB. Direct navigation is safe from
 * every state (logged-out, unbound, paused, rejected, disabled).
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { getAffiliatePortalState } from "@/lib/affiliate/portal.functions";
import { bindMyAffiliateAccount } from "@/lib/affiliate/identity.functions";

export const Route = createFileRoute("/affiliate/status")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Affiliate status · TruWe" },
      {
        name: "description",
        content: "TruWe Affiliate application status.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AffiliateStatusPage,
});

function AffiliateStatusPage() {
  const navigate = useNavigate();
  const fetchState = useServerFn(getAffiliatePortalState);
  const bind = useServerFn(bindMyAffiliateAccount);

  // If unauthenticated, show login CTA card (no redirect — direct-route safe).
  const authQuery = useQuery({
    queryKey: ["affiliate", "session"],
    queryFn: async () => {
      const { data } = await supabase.auth.getUser();
      return { authenticated: !!data.user };
    },
    staleTime: 30_000,
  });

  const stateQuery = useQuery({
    queryKey: ["affiliate", "portal-state"],
    queryFn: () => fetchState(),
    enabled: authQuery.data?.authenticated === true,
    staleTime: 15_000,
  });

  useEffect(() => {
    if (stateQuery.data?.state === "approved_bound") {
      navigate({ to: "/affiliate/dashboard", replace: true });
    }
  }, [stateQuery.data, navigate]);

  if (authQuery.isLoading) return <StatusShell><p>Loading…</p></StatusShell>;

  if (!authQuery.data?.authenticated) {
    return (
      <StatusShell>
        <h2 className="font-display text-xl font-semibold text-brand-deep">
          Sign in to view your status
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Affiliate portal access uses your WhatsApp mobile number.
        </p>
        <Button asChild className="mt-6">
          <Link to="/affiliate/login">Sign in with WhatsApp</Link>
        </Button>
      </StatusShell>
    );
  }

  if (stateQuery.isLoading || !stateQuery.data) {
    return <StatusShell><p>Checking your application…</p></StatusShell>;
  }

  const st = stateQuery.data.state;

  if (st === "disabled") {
    return (
      <StatusShell>
        <h2 className="font-display text-xl font-semibold text-brand-deep">
          Affiliate programme is not yet live
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          We&apos;re preparing the TruWe Affiliate programme. When it launches,
          you&apos;ll be able to apply from this page.
        </p>
      </StatusShell>
    );
  }

  if (st === "not_applied") {
    return (
      <StatusShell>
        <h2 className="font-display text-xl font-semibold text-brand-deep">
          You haven&apos;t applied yet
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Fill in the short application to join the TruWe Affiliate programme.
        </p>
        <Button asChild className="mt-6">
          <Link to="/affiliate/apply">Start application</Link>
        </Button>
      </StatusShell>
    );
  }

  if (st === "pending") {
    return (
      <StatusShell>
        <h2 className="font-display text-xl font-semibold text-brand-deep">
          Application under review
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Thanks, we&apos;ll email you when the review is complete. Typical
          turnaround is 3-5 working days.
        </p>
      </StatusShell>
    );
  }

  if (st === "approved_unbound") {
    return (
      <StatusShell>
        <h2 className="font-display text-xl font-semibold text-brand-deep">
          Your application is approved
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Confirm the WhatsApp number on your application to activate your
          dashboard.
        </p>
        <Button
          className="mt-6"
          onClick={async () => {
            try {
              await bind();
            } finally {
              await stateQuery.refetch();
            }
          }}
        >
          Activate my dashboard
        </Button>
      </StatusShell>
    );
  }

  if (st === "rejected") {
    return (
      <StatusShell>
        <h2 className="font-display text-xl font-semibold text-brand-deep">
          Your application wasn&apos;t accepted
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Thanks for your interest. You&apos;re welcome to apply again in 90 days.
        </p>
      </StatusShell>
    );
  }

  if (st === "paused") {
    return (
      <StatusShell>
        <h2 className="font-display text-xl font-semibold text-brand-deep">
          Your account is paused
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Your affiliate account is temporarily paused. Please email{" "}
          <a className="underline" href="mailto:affiliates@truwe.in">
            affiliates@truwe.in
          </a>{" "}
          if you have questions.
        </p>
      </StatusShell>
    );
  }

  if (st === "reconsent_required") {
    return (
      <StatusShell>
        <h2 className="font-display text-xl font-semibold text-brand-deep">
          Re-accept the Affiliate Partner Agreement
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          We&apos;ve updated the Affiliate Partner Agreement to version{" "}
          <strong>{stateQuery.data.currentTermsVersion}</strong>. Please review
          and re-accept from the application page to continue.
        </p>
        <div className="mt-6 flex gap-3">
          <Button asChild variant="outline">
            <Link to="/policies/affiliate-terms">Read the agreement</Link>
          </Button>
          <Button asChild>
            <Link to="/affiliate/apply">Re-accept</Link>
          </Button>
        </div>
      </StatusShell>
    );
  }

  // approved_bound falls through to redirect above; render a hint just in case.
  return (
    <StatusShell>
      <p>Opening your dashboard…</p>
    </StatusShell>
  );
}

function StatusShell({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto max-w-xl px-4 py-14">
      <div className="rounded-2xl border border-border bg-card p-8 shadow-sm">
        {children}
      </div>
    </main>
  );
}
